import { Inject, Injectable } from "@angular/core";
import { NavigationHierarchyService } from "@common/route/navigation-hierarchy.service";
import { CommonSidebarConstants } from "@common/shell/common-sidebar/common-sidebar.constants";
import { ISidebarTab, SidebarTabPosition } from "@common/shell/common-sidebar/sidebar-tab";
import { SidebarTabIconComponent } from "@common/shell/sidebar-tab-icon/sidebar-tab-icon.component";
import { IComponentRender } from "@common/ux/render-component/component-render.interface";
import { map, Observable, of, pairwise, startWith, Subject, take, tap } from "rxjs";
import { CHAT_AI_ORG_PROVIDER, IChatAiOrgProvider } from "./chat-ai-org-provider.interface";
import { ChatAiSidebarContentComponent } from "./chat-ai-sidebar-content/chat-ai-sidebar-content.component";
import { ChatAiSidebarTabId, ChatAiUiService, ChatLocationMode } from "./chat-ai-ui.service";

export { ChatAiSidebarTabId };
export const ChatAiSidebarTabIcon = "fal fa-fw fa-stars";

@Injectable()
export class ChatAiSidebarTab implements ISidebarTab {
    public id = ChatAiSidebarTabId;
    public label = "Lumia Assistant";
    public ordinal = 4;
    public position = SidebarTabPosition.Bottom;
    public isLoading$: Observable<boolean>;
    public isDisplayed$: Observable<boolean>;
    public minWidth = CommonSidebarConstants.ChatAiTabMinWidth;

    private triggerFocus$ = new Subject<void>(); // init before being used by focusTab$
    public focusTab$ = this.triggerFocus$.asObservable();

    public icon: IComponentRender<SidebarTabIconComponent> = {
        component: SidebarTabIconComponent,
        params: {
            iconClass: ChatAiSidebarTabIcon,
            iconText: "AI Assist",
        },
    };

    public content: IComponentRender<ChatAiSidebarContentComponent> = {
        component: ChatAiSidebarContentComponent,
    };

    public constructor(
        @Inject(CHAT_AI_ORG_PROVIDER) orgService: IChatAiOrgProvider,
        private chatUiService: ChatAiUiService,
        navHierarchyService: NavigationHierarchyService,
    ) {
        chatUiService.hasChatAiSidebarTab = true;
        this.isLoading$ = of(false);
        this.isDisplayed$ = chatUiService.isFeatureEnabledForCurrentBreakpoint$();

        chatUiService.isFeatureEnabledForLocationMode$(ChatLocationMode.Sidebar).pipe(
            startWith(undefined),
            pairwise(),
            map(([previous, current]) => {
                if (!current && previous === true) {
                    // so it won't leave behind the blank sidebar content when the tab is closed
                    // only do this if it was previously open
                    navHierarchyService.updateActiveNodeFromUrl();
                    orgService.notifyOrganisationChangedEvent();
                } else if (current) {
                    if (chatUiService.isSwitchingLocationMode) {
                        // switch from dialog to sidebar -> focus the tab
                        chatUiService.openSidebar();
                    }
                }

                return !!current;
            })
        ).subscribe();
    }

    public onClick(event: CustomEvent) {
        return this.chatUiService.chatLocationMode$.pipe(
            take(1),
            tap((chatLocation) => {
                if (chatLocation === ChatLocationMode.ApplicationBarDialog) {
                    event.preventDefault();
                    this.chatUiService.triggerChatDialog();
                } else {
                    this.chatUiService.emitChatVisibleChange();
                }
            }),
        );
    }
}
